import { useState } from 'react';
import { InfiniteTable } from '@/components/_common';
import ItemsSelectedModal from '@/components/_common/ItemsSelectedModal';
import {
  PurchaseImportItemType,
  PurchaseItems,
} from '@/core/domain/purchase/purchaseImport/PurchaseImport.types';
import { RequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import { getTableScrollX } from '@/utils/table';
import { Divider, Space, Switch, Tooltip, Typography, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { FaInfoCircle, FaLink, FaUnlink } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';

import { RegistrationsDefaultCode } from '@/types/Register';
import { v4 } from 'uuid';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props {
  companyId?: number;
  filePurchaseItems?: PurchaseItems[];
  purchaseItems: PurchaseItems[];
  setPurchaseItems: React.Dispatch<React.SetStateAction<PurchaseItems[]>>;
}

const { Title } = Typography;

const ConfirmImportPurchaseItemsTable: React.FC<Props> = ({
  companyId,
  filePurchaseItems = [],
  purchaseItems,
  setPurchaseItems,
}) => {
  const { isMobile } = useWindowSize();
  const { data } = useMatrix();
  const intl = useIntl();

  const [itemIndex, setItemIndex] = useState<number | null>(null);

  const [modalItemsOpened, modalItemsAction] = useDisclosure(false);

  const titleProductCode = () => {
    if (data) {
      if (
        data?.matrixSettings.defaultPartCode ===
        RegistrationsDefaultCode.barCode
      ) {
        return 'Cód. Barras';
      }
      if (
        data?.matrixSettings.defaultPartCode ===
        RegistrationsDefaultCode.manufacturerCode
      ) {
        return 'Cód. Fabricante';
      }
      if (
        data?.matrixSettings.defaultPartCode ===
        RegistrationsDefaultCode.internalCode
      ) {
        return 'Cód Interno';
      }
      return 'Cód Empresa';
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function changeFieldItem(key: [number, keyof PurchaseItems], value: any) {
    setPurchaseItems((state) =>
      state.map((item, index) =>
        key[0] === index ? { ...item, [key[1]]: value } : item,
      ),
    );
  }

  function changeItem(item: PurchaseItems, index: number) {
    setPurchaseItems((state) =>
      state.map((prevItem, i) => (i === index ? item : prevItem)),
    );
  }

  function addItemAfter(item: PurchaseItems, index: number) {
    setPurchaseItems((state) => [
      ...state.slice(0, index),
      item,
      ...state.slice(index),
    ]);
  }

  function removeItem(index: number) {
    setPurchaseItems((state) => state.filter((_, i) => i !== index));
  }

  const linkingItem = async (item?: StockItem, itemOs?: RequestPurchases) => {
    modalItemsAction.close();

    if (item && typeof itemIndex === 'number') {
      const itemType = item.partId
        ? PurchaseImportItemType.part
        : PurchaseImportItemType.consumable;

      changeItem(
        {
          ...purchaseItems[itemIndex],
          type: itemType,
          name: item.description,
          productCode:
            data?.matrixSettings.defaultPartCode ===
            RegistrationsDefaultCode.manufacturerCode
              ? item.manufacturingCode
              : item.barcode,
          internalCode: item.sku,
          partDTO: {
            id: item.partId,
            barCode: item.barcode,
            companyCode: item.companyCode,
            sku: item.sku,
            manufacturingCode: item.manufacturingCode,
          },
          consumableDTO: {
            id: item.consumableId,
            barCode: item.barcode,
            sku: item.sku,
            manufacturingCode: item.manufacturingCode,
          },
          manualLink: true,
          linked: true,
          isAlter: false,
          checked: itemType === PurchaseImportItemType.part,
        } as PurchaseItems,
        itemIndex,
      );
    } else if (itemOs && typeof itemIndex === 'number') {
      let quantity = itemOs.requestedQuantity;

      const purchaseItemFinded = purchaseItems[itemIndex];

      const itemClone = {
        ...purchaseItemFinded,
        quantity,
        totalAmount:
          quantity * purchaseItemFinded.unitaryValue -
          (purchaseItemFinded.discount || 0),
        type: PurchaseImportItemType.part,
        name: itemOs.description,
        productCode: itemOs.item.manufacturingCode,
        partDTO: {
          id: itemOs.item.id,
          manufacturingCode: itemOs.item.manufacturingCode,
        },
        workOrderNumber: itemOs.workOrderNumber,
        workOrderItemId: itemOs.workItemId,
        complementId: itemOs.complementId,
        workOrderPurchaseQuotations: {
          id: itemOs.id,
          quantitypurchased: itemOs.quantityPurchased,
          quantitySentPurchase: itemOs.quantitySentPurchased,
          requestedQuantity: itemOs.requestedQuantity,
        },
        manualLink: true,
        linked: true,
        isAlter: false,
        checked: true,
      } as PurchaseItems;

      quantity = purchaseItemFinded.quantity - itemOs.requestedQuantity;

      if (itemOs.requestedQuantity < purchaseItemFinded.quantity) {
        changeItem(
          {
            ...purchaseItemFinded,
            quantity,
            totalAmount:
              quantity * purchaseItemFinded.unitaryValue -
              (purchaseItemFinded.discount || 0),
          } as PurchaseItems,
          itemIndex,
        );

        addItemAfter(
          { ...itemClone, key: v4(), cloneKey: purchaseItemFinded.key },
          itemIndex,
        );
      } else {
        changeItem(
          {
            ...itemClone,
            quantity: purchaseItemFinded.quantity,
            totalAmount:
              purchaseItemFinded.quantity * purchaseItemFinded.unitaryValue -
              (purchaseItemFinded.discount || 0),
          },
          itemIndex,
        );
      }
    }

    setItemIndex(null);
  };

  async function unlinkItem(key: string) {
    try {
      const purchaseItemFindedIndex = purchaseItems.findIndex(
        (item) => item.key === key,
      );
      const purchaseItemFinded = purchaseItems[purchaseItemFindedIndex];

      const filePurchaseItemFinded =
        filePurchaseItems[purchaseItemFinded.index];

      if (purchaseItemFinded.cloneKey) {
        const originalPurchaseItemIndex = purchaseItems.findIndex(
          (item) => item.key === purchaseItemFinded.cloneKey,
        );
        const originalPurchaseItem = purchaseItems[originalPurchaseItemIndex];
        const quantity =
          (originalPurchaseItem?.quantity || 0) + purchaseItemFinded.quantity;

        changeItem(
          {
            ...originalPurchaseItem,
            quantity,
            totalAmount:
              quantity * originalPurchaseItem.unitaryValue -
              (originalPurchaseItem.discount || 0),
          } as PurchaseItems,
          originalPurchaseItemIndex,
        );

        removeItem(purchaseItemFindedIndex);
      } else if (
        purchaseItemFinded.partDTO ||
        purchaseItemFinded.consumableDTO
      ) {
        changeItem(
          {
            ...purchaseItemFinded,
            ...filePurchaseItemFinded,
            quantity: purchaseItemFinded.quantity,
            totalAmount:
              purchaseItemFinded.quantity * purchaseItemFinded.unitaryValue -
              (purchaseItemFinded.discount || 0),
            consumableDTO: undefined,
            partDTO: undefined,
            isAlter: true,
            linked: false,
            checked: purchaseItemFinded.type === PurchaseImportItemType.part,
          } as PurchaseItems,
          purchaseItemFindedIndex,
        );
      } else {
        const quantity = purchaseItemFinded.quantity;

        changeItem(
          {
            ...purchaseItemFinded,
            ...filePurchaseItemFinded,
            index: purchaseItemFinded.index,
            quantity,
            type: filePurchaseItemFinded.type,
            name: filePurchaseItemFinded.name,
            productCode: filePurchaseItemFinded.productCode,
            internalCode: filePurchaseItemFinded?.internalCode,
            partDTO: filePurchaseItemFinded.partDTO,
            consumableDTO: filePurchaseItemFinded.consumableDTO,
            workOrderNumber: filePurchaseItemFinded.workOrderNumber,
            workOrderItemId: filePurchaseItemFinded.workOrderItemId,
            complementId: filePurchaseItemFinded.complementId,
            workOrderPurchaseQuotations:
              filePurchaseItemFinded.workOrderPurchaseQuotations,
            totalAmount:
              quantity * purchaseItemFinded.unitaryValue -
              (purchaseItemFinded.discount || 0),
            manualLink: false,
            isAlter:
              filePurchaseItemFinded?.partDTO ||
              filePurchaseItemFinded?.consumableDTO
                ? false
                : true,
            linked:
              filePurchaseItemFinded?.partDTO ||
              filePurchaseItemFinded?.consumableDTO
                ? true
                : false,
            checked:
              filePurchaseItemFinded?.type === PurchaseImportItemType.part,
          } as PurchaseItems,
          purchaseItemFindedIndex,
        );
      }
    } catch {
      message.warning(
        'Não foi possível remover o link da peça, tente novamente.',
      );
    }
  }

  const columns: ColumnsType<PurchaseItems> = [
    {
      dataIndex: 'type',
      title: 'Peça/Insumo',
      ellipsis: true,
      width: 100,
      render: (_value, row, index) => {
        return (
          <Switch
            disabled={row.linked}
            checked={row.checked}
            checkedChildren="Peça"
            unCheckedChildren="Insumo"
            onChange={(checked) => changeFieldItem([index, 'checked'], checked)}
          />
        );
      },
    },
    {
      title: titleProductCode(),
      ellipsis: true,
      width: 110,
      render: (_, record) => {
        if (
          data?.matrixSettings.defaultPartCode ===
          RegistrationsDefaultCode.internalCode
        ) {
          return record.linked ? record.partDTO?.sku : '-';
        } else if (
          data?.matrixSettings.defaultPartCode ===
          RegistrationsDefaultCode.barCode
        ) {
          return record.linked ? record.partDTO?.barCode : record.barCode;
        } else if (
          data?.matrixSettings.defaultPartCode ===
          RegistrationsDefaultCode.manufacturerCode
        ) {
          return record.linked
            ? record.partDTO?.manufacturingCode
            : record.productCode;
        } else if (
          data?.matrixSettings.defaultPartCode ===
          RegistrationsDefaultCode.companyCode
        ) {
          return record.linked ? record.partDTO?.companyCode : '-';
        }
      },
    },
    {
      dataIndex: 'name',
      title: 'Descrição',
      ellipsis: true,
      width: 260,
      render: (value, record) => {
        if (
          (record.partDTO?.id ||
            record.consumableDTO?.id ||
            record.partId ||
            record.consumableId) &&
          record.linked
        ) {
          return record.manualLink
            ? value
            : record.partDTO?.name ?? record.consumableDTO?.name;
        } else {
          return value;
        }
      },
    },
    {
      dataIndex: 'workOrderNumber',
      title: 'O.S.',
      render: (value) => value ?? '-',
      ellipsis: true,
      width: 100,
    },
    {
      dataIndex: 'quantity',
      title: 'Qtd.',
      render: (value) => quantityFormatter(value),
      ellipsis: true,
      width: 90,
    },
    {
      dataIndex: 'unitaryValue',
      title: 'Pr. Unit. (R$)',
      render: (value) => formatterPrice(value),
      ellipsis: true,
      width: 120,
    },
    {
      dataIndex: 'discount',
      title: 'Desc.(R$)',
      render: (value) => `R$ ${quantityFormatter(value)}`,
      ellipsis: true,
      width: 100,
    },
    {
      dataIndex: 'totalAmount',
      title: 'Vr. total liquido (R$)',
      render: (value) => `R$ ${quantityFormatter(value)}`,
      ellipsis: true,
      width: 140,
    },
    {
      key: 'actions',
      width: 100,
      fixed: 'right',
      align: 'right',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      render: (_, row, index) => {
        if (row.isAlter) {
          return (
            <Space>
              {!!row.duplicatePartBarcode && !row.linked && (
                <>
                  <Tooltip
                    trigger={isMobile ? 'click' : 'hover'}
                    title={`Para evitar duplicidade de registros, o produto será cadastrado com o código de barras 'SEM GTIN', uma vez que já consta na base de dados com o código de barras '${row.barCode}'.`}
                  >
                    <FaInfoCircle color="orange" size={20} />
                  </Tooltip>

                  <Divider type="vertical" />
                </>
              )}

              {!row.linked && (
                <Tooltip
                  trigger={isMobile ? 'click' : 'hover'}
                  title={`Vincular item`}
                >
                  <FaLink
                    size={16}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setItemIndex(index);
                      modalItemsAction.open();
                    }}
                  />
                </Tooltip>
              )}

              {!!row.linked && (
                <Tooltip
                  trigger={isMobile ? 'click' : 'hover'}
                  title={'Remover Vinculo'}
                >
                  <FaUnlink
                    size={16}
                    style={{ cursor: 'pointer' }}
                    cursor="pointer"
                    onClick={() => unlinkItem(row.key)}
                  />
                </Tooltip>
              )}
            </Space>
          );
        }

        return (
          !!row.linked && (
            <Tooltip
              trigger={isMobile ? 'click' : 'hover'}
              title={'Remover Vinculo'}
            >
              <FaUnlink
                size={16}
                style={{ cursor: 'pointer' }}
                cursor="pointer"
                onClick={() => unlinkItem(row.key)}
              />
            </Tooltip>
          )
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginTop: '16px' }}>
        <Title level={3}>Itens da Compra</Title>
      </div>

      <InfiniteTable
        dataSource={purchaseItems ?? []}
        columns={columns}
        rowKey={(row) => row.key}
        scroll={{ x: getTableScrollX(columns) }}
        loading={!purchaseItems}
      />

      <ItemsSelectedModal
        itemsSelected={purchaseItems
          ?.filter((item) => !!item.linked && !!item.partDTO?.id)
          ?.map(
            (item) =>
              ({
                partId: item.partDTO?.id,
                complementId: item.complementId,
              } as {
                partId: number;
                complementId?: number;
              }),
          )}
        handleVisible={modalItemsAction.toggle}
        visible={modalItemsOpened}
        onSelectItem={linkingItem}
        companyId={companyId}
      />
    </div>
  );
};

export default ConfirmImportPurchaseItemsTable;
